// Meldingen page — één duidelijk overzicht van al je notificatie-voorkeuren.
// Thema en taal staan in Profiel, hier alleen meldingen.
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { PageHeader } from "@/components/ui-bits";
import {
  ArrowLeft, Bell, BellOff, Zap, ClipboardList, Repeat, Megaphone, Cake,
  CalendarDays, Smartphone, Mail, BellRing, Check, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { pushSupported, enablePush, disablePush, getCurrentSubscription, sendTestPush } from "@/lib/push";

const CATEGORIES = (t) => [
  { key: "leave",    Icon: ClipboardList, tint: "217 91% 55%", label: t("cat_leave"),    desc: t("cat_leave_desc") },
  { key: "swap",     Icon: Repeat,        tint: "280 65% 55%", label: t("cat_swap"),     desc: t("cat_swap_desc") },
  { key: "roster",   Icon: CalendarDays,  tint: "160 65% 45%", label: t("cat_roster"),   desc: t("cat_roster_desc") },
  { key: "news",     Icon: Megaphone,     tint: "30 92% 50%",  label: t("cat_news"),     desc: t("cat_news_desc") },
  { key: "birthday", Icon: Cake,          tint: "340 80% 55%", label: t("cat_birthday"), desc: t("cat_birthday_desc") },
];

export default function Preferences() {
  const { t } = useI18n();
  const navigate = useNavigate();

  // ---- Push subscription state ---------------------------------------------
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  useEffect(() => {
    if (!pushSupported()) return;
    (async () => setPushEnabled(!!(await getCurrentSubscription())))();
  }, []);
  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) { await disablePush(); setPushEnabled(false); toast.success(t("notifs_off_toast")); }
      else { await enablePush(); setPushEnabled(true); toast.success(t("notifs_on_toast")); }
    } catch (e) { toast.error(e.message || t("action_failed")); }
    finally { setPushBusy(false); }
  };
  const testPush = async () => {
    try { await sendTestPush(); toast.success(t("test_sent")); }
    catch { toast.error(t("test_failed")); }
  };

  // ---- Notification prefs (backend) ----------------------------------------
  const [prefs, setPrefs] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/users/me/notification-prefs"); setPrefs(data); }
      catch { setPrefs(null); }
    })();
  }, []);
  const patchPref = async (cat, patch) => {
    const cur = prefs[cat] || { app: true, email: true, push: true };
    const next = { ...cur, ...patch };
    setPrefs({ ...prefs, [cat]: next });
    try { await api.patch("/users/me/notification-prefs", { [cat]: patch }); }
    catch { toast.error(t("couldnt_save")); setPrefs({ ...prefs, [cat]: cur }); }
  };
  const toggleChannel = (cat, chan) => {
    const cur = prefs[cat] || { app: true, email: true, push: true };
    patchPref(cat, { [chan]: !cur[chan] });
  };
  const setMaster = (cat, on) => {
    patchPref(cat, { app: on, email: on, push: on });
  };

  // ---- Stats (top hint) ----------------------------------------------------
  const enabledCounts = prefs
    ? Object.entries(prefs).filter(([k, v]) => k !== "generic" && (v.app || v.email || v.push)).length
    : 0;
  const totalCats = CATEGORIES(t).length;

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto space-y-5 pb-24" data-testid="page-preferences">
      <button onClick={() => navigate(-1)} className="btn btn-ghost !h-9 !text-[12px]" data-testid="pref-back">
        <ArrowLeft size={13} /> {t("back")}
      </button>
      <PageHeader
        eyebrow={t("preferences_label")}
        title={t("notifications_page_title")}
        subtitle={t("notifications_page_subtitle")}
      />

      {/* Overview stat pill */}
      <motion.div
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
        className="card p-4 md:p-5 flex items-center gap-4"
        data-testid="pref-overview"
      >
        <div className="w-11 h-11 rounded-2xl bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))] grid place-items-center shrink-0">
          <Bell size={17} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] leading-tight">
            {enabledCounts}/{totalCats} {t("pref_categories")}
          </div>
          <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5">
            {enabledCounts === 0 ? t("pref_all_off") : enabledCounts === totalCats ? t("pref_all_on") : t("pref_some_on")}
          </div>
        </div>
      </motion.div>

      {/* Push on this device */}
      {pushSupported() && (
        <motion.div
          initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }}
          className="card p-5"
          data-testid="pref-push-card"
        >
          <div className="flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0
              ${pushEnabled ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"}`}>
              {pushEnabled ? <BellRing size={17} strokeWidth={1.8} /> : <BellOff size={17} strokeWidth={1.8} />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] leading-tight">
                {t("push_on_device")}
              </div>
              <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">
                {pushEnabled ? t("push_active") : t("push_allow")}
              </div>
            </div>
            <Switch
              checked={pushEnabled}
              onChange={togglePush}
              disabled={pushBusy}
              testid="pref-push-toggle"
            />
          </div>
          {pushEnabled && (
            <button
              onClick={testPush}
              className="btn btn-ghost !h-9 !text-[12px] mt-4 w-full"
              data-testid="pref-push-test"
            >
              <Zap size={13} /> {t("test_notification")}
            </button>
          )}
        </motion.div>
      )}

      {/* Categories */}
      {prefs && (
        <div className="flex flex-col gap-3">
          <div className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-muted))] font-semibold px-1 mt-1">
            {t("notification_types")}
          </div>
          {CATEGORIES(t).map(({ key, Icon, tint, label, desc }, i) => {
            const p = prefs[key] || { app: true, email: true, push: true };
            const anyOn = p.app || p.email || p.push;
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.03 * i }}
                className={`card overflow-hidden transition-opacity ${anyOn ? "" : "opacity-70"}`}
                data-testid={`pref-cat-${key}`}
              >
                {/* Master header */}
                <div className="p-4 md:p-5 flex items-center gap-4">
                  <div
                    className="w-11 h-11 rounded-2xl grid place-items-center shrink-0"
                    style={{ background: `hsl(${tint} / 0.14)`, color: `hsl(${tint})` }}
                  >
                    <Icon size={17} strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-[15px] font-bold text-[hsl(var(--text-strong))] leading-tight">
                      {label}
                    </div>
                    <div className="text-[12px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">
                      {desc}
                    </div>
                  </div>
                  <Switch
                    checked={anyOn}
                    onChange={() => setMaster(key, !anyOn)}
                    testid={`pref-cat-${key}-master`}
                  />
                </div>

                {/* Channels — reveal only when master is ON */}
                <AnimatePresence initial={false}>
                  {anyOn && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: "easeOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 md:px-5 pb-4 md:pb-5 border-t hairline">
                        <div className="pt-4 flex flex-col gap-1">
                          <ChannelRow
                            Icon={Smartphone}
                            label={t("channel_app")}
                            hint={t("channel_app_hint")}
                            checked={p.app}
                            onToggle={() => toggleChannel(key, "app")}
                            testid={`pref-cat-${key}-app`}
                          />
                          <ChannelRow
                            Icon={Mail}
                            label={t("channel_email")}
                            hint={t("channel_email_hint")}
                            checked={p.email}
                            onToggle={() => toggleChannel(key, "email")}
                            testid={`pref-cat-${key}-email`}
                          />
                          <ChannelRow
                            Icon={BellRing}
                            label={t("channel_push")}
                            hint={t("channel_push_hint")}
                            checked={p.push}
                            onToggle={() => toggleChannel(key, "push")}
                            testid={`pref-cat-${key}-push`}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Footer note */}
      <div className="rounded-2xl border hairline p-4 flex items-start gap-3" style={{ background: "hsl(var(--muted) / 0.5)" }}>
        <div className="w-8 h-8 rounded-lg bg-[hsl(var(--surface))] grid place-items-center shrink-0">
          <Bell size={13} className="text-[hsl(var(--primary))]" strokeWidth={2.2} />
        </div>
        <div className="text-[12px] leading-relaxed text-[hsl(var(--text-muted))]">
          {t("pref_footer_hint")}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

// Big, tappable iOS-style switch
function Switch({ checked, onChange, disabled, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      data-testid={testid}
      className={`relative shrink-0 h-8 w-14 rounded-full transition-colors duration-200 ease-out
        ${checked ? "bg-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] border hairline"}
        ${disabled ? "opacity-60" : ""}`}
    >
      <motion.span
        layout
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
        className={`absolute top-1 h-6 w-6 rounded-full bg-white grid place-items-center shadow-[0_2px_6px_rgba(0,0,0,0.18)]
          ${checked ? "right-1" : "left-1"}`}
      >
        {checked
          ? <Check size={12} strokeWidth={3} className="text-[hsl(var(--primary))]" />
          : <XIcon size={11} strokeWidth={3} className="text-[hsl(var(--text-muted))]" />
        }
      </motion.span>
    </button>
  );
}

// Full-width row per channel with hint + big toggle
function ChannelRow({ Icon, label, hint, checked, onToggle, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={`flex items-center gap-3.5 rounded-2xl px-3 py-2.5 text-left transition
        ${checked ? "bg-[hsl(var(--primary-soft)_/_0.5)]" : "hover:bg-[hsl(var(--muted))]"}`}
    >
      <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0
        ${checked ? "bg-white text-[hsl(var(--primary))]" : "bg-[hsl(var(--muted))] text-[hsl(var(--text-muted))]"}`}>
        <Icon size={14} strokeWidth={1.9} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[hsl(var(--text-strong))] leading-tight">{label}</div>
        <div className="text-[11.5px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug line-clamp-2">{hint}</div>
      </div>
      <Switch checked={checked} onChange={onToggle} testid={`${testid}-switch`} />
    </button>
  );
}
